'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { redirect } from 'next/navigation';
import { tenant } from '@/interfaces/common';
import { otpFrom } from '@/utils/constants';
import toast from 'react-hot-toast';
import { IoLogoLinkedin } from 'react-icons/io5';

import postMessageToParent from '@/lib/postMessage';
import useEmailCheck from '@/hooks/useEmailCheck';
import useOtpSend from '@/hooks/useOtpSend';
import useOtpVerify from '@/hooks/useOtpVerify';
import useSessionStorageInfo from '@/hooks/useSessionStorageInfo';
import { Button } from '@/components/ui';

import EmailForm from '../../Login/sections/EmailForm';
import OTPForm from '../../Login/sections/OTPForm';
import OTPSuccess from './OTPSuccess';

const EMAIL_NOT_FOUND = 'Email not found!';
const EMAIL_NOT_SIGNEDUP_WITH_TENANT = 'Email not signed up with this tenant!';

const SignupForm = ({ tenant }: { tenant: tenant }) => {
  const [isSuccessOTP] = useState(false);
  const [isEmailForm, setIsEmailForm] = useState(true);
  const [email, setEmail] = useState('');
  const [wrongEmail, setWrongEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [otpErroMsg, setOtpErrorMsg] = useState('');

  const { redirectURL, postParent, tenantData, clientId } =
    useSessionStorageInfo();

  const REQUEST_FROM_SIGNUP = true;
  const { loading, data, error, handleClick } = useEmailCheck({
    REQUEST_FROM_SIGNUP,
  });
  const [errorMsg, setErrorMsg] = useState(error);

  const {
    loading: otpLoading,
    data: otpResponse,
    error: otpError,
    handleClick: sendOtp,
  } = useOtpSend();

  const {
    loading: otpVerifyLoading,
    data: otpVerifyResponse,
    handleClick: handleOtpVerify,
    error: otpVerifyError,
  } = useOtpVerify();

  const handleVerify = () => {
    handleOtpVerify(email, tenant?._id, otp);
  };

  useEffect(() => {
    if (otpVerifyResponse) {
      if (otpVerifyResponse.success) {
        if (postParent) {
          postMessageToParent({ ...otpVerifyResponse, client_id: clientId });
          redirect('/successful-login');
        }

        if (redirectURL) {
          window.location.replace(
            `${redirectURL}?accessToken=${otpVerifyResponse.accessToken}&refreshToken=${otpVerifyResponse.refreshToken}&tenantId=${tenantData?._id}&tenantName=${tenantData?.tenantName}&clientId=${clientId}`
          );
        }
      } else {
        toast.error(otpVerifyResponse.message);
        setOtpErrorMsg(otpVerifyResponse.message);
      }
    }
  }, [otpVerifyResponse]);

  const handleSubmit = (e: any) => {
    e.preventDefault();
    handleClick(email, tenant?._id);
  };

  useEffect(() => {
    if (data?.isUserInSameTenant) {
      const errorText =
        'Email already exists with the same tenant, please login.';
      setErrorMsg(errorText);
      toast.error(errorText);
      setIsEmailForm(true);
    } else if (data?.isUserInSameTenant === false) {
      sendOtp(email, tenant?._id, otpFrom.SIGNUP, true, wrongEmail);
    }
  }, [data]);

  useEffect(() => {
    if (error === EMAIL_NOT_FOUND || error === EMAIL_NOT_SIGNEDUP_WITH_TENANT) {
      setErrorMsg('');
      sendOtp(email, tenant?._id, otpFrom.SIGNUP, true, wrongEmail);
    } else {
      setErrorMsg(error);
    }
  }, [error]);

  useEffect(() => {
    if (otpResponse?.message) {
      setErrorMsg('');
      setOtpErrorMsg('');
      toast.success(otpResponse.message);
      setIsEmailForm(false);
    }
  }, [otpResponse]);

  useEffect(() => {
    if (otpError) {
      toast.error(otpError);
      setOtpErrorMsg(otpError);
    }

    if (otpVerifyError) {
      toast.error(otpVerifyError);
      setOtpErrorMsg(otpVerifyError);
    }
  }, [otpError, otpVerifyError]);

  if (isSuccessOTP) {
    return <OTPSuccess />;
  }

  const handleRevertBackToEmailForm = () => {
    setIsEmailForm(true);
    setWrongEmail(email);
    setEmail('');
  };

  return (
    <div
      className="flex items-center justify-center overflow-y-auto rounded-3xl bg-white"
      style={{ height: 'calc(100vh - 48px)' }}
    >
      <div className="w-[384px]">
        {tenant?.logo && (
          <Image
            src={tenant?.logo}
            alt={tenant?.tenantName}
            width={120}
            height={53}
          />
        )}

        <h1 className="mb-4 mt-12 text-3xl font-bold">
          {isEmailForm
            ? 'Start Your Design Validation Journey!'
            : 'Verify your email address'}
        </h1>
        {isEmailForm && (
          <p className="mb-8 text-sm font-normal text-zinc-500">
            Discover the Ideal IC for Your Design with Ease. Unlock the Power of
            Real Hardware in the Cloud with LiveBench.{' '}
          </p>
        )}

        {isEmailForm ? (
          <>
            <EmailForm
              loading={loading || otpLoading}
              email={email}
              setEmail={setEmail}
              handleSubmit={handleSubmit}
              fromSignup={true}
              errorMsg={errorMsg}
            />
            {tenantData?.linkedin && (
              <a
                href={
                  tenantData?._id
                    ? `${process.env.NEXT_PUBLIC_CW_AUTH_URL}/linkedin`
                    : '#'
                }
              >
                <Button className="mt-2" variant="outline" size="fullWidth">
                  <IoLogoLinkedin className="mr-2 h-[15px] w-[15px]" /> Get
                  started with Linkedin
                </Button>
              </a>
            )}

            <p className="mt-4 text-center text-xs text-zinc-500">
              By continuing, you agree to {tenantData.tenantName} Lab’s{' '}
              <Link className="underline" href="/">
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link className="underline" href="/">
                Privacy Policy
              </Link>
            </p>
            <p className="cursor-pointer pt-6 text-center text-sm text-zinc-500">
              Have an account?{' '}
              <Link
                className="font-medium text-primary-950"
                href={`/${tenantData?._id !== undefined ? `?tenantId=${tenantData?._id}` : ''}`}
              >
                Login
              </Link>
            </p>
          </>
        ) : (
          <OTPForm
            loading={otpVerifyLoading}
            email={email}
            value={otp}
            setValue={setOtp}
            handleVerify={handleVerify}
            setIsEmailForm={setIsEmailForm}
            fromSignup={true}
            tenantId={tenant?._id}
            handleRevertBackToEmailForm={handleRevertBackToEmailForm}
            errorMsg={otpErroMsg}
            setErrorMsg={setOtpErrorMsg}
          />
        )}
      </div>
    </div>
  );
};

export default SignupForm;
